// A live-rig bundle on the next path: one control vector, every active region re-decoded.
// Notes: ~/comments/aos-threejs-poc/LiveRigAvatar_start_and_face_push.md

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { getPluginNode } from '../../lib/pluginNodeRegistry.js'
import { resolveProviders } from '../../lib/ortSession.js'
import { setContentFramer, fitCameraToSphere } from '../../lib/viewportFraming.js'
import { reportDowngrade, reportPrecision, reportRigProblem } from '../../lib/ogsAssetStatus.js'
import { registerRigBoard, clearRigBoard, setRigBoardRawLayer } from '../../ogs/rig/rigBoardSource.js'
import { gatherFromRigNames } from '../../ogs/rig/rigGatherFromNames.js'
import { setSplatEyeFollowCapability } from '../../lib/eyeFollowSupport.js'
import * as ogsOrt from '../../ogs/inference/ort.ts'
import { ogsGpuFlags } from '../../ogs/inference/ogsGpuFlags.ts'
import { chooseLift } from '../../ogs/inference/myra/myraLiftGate.ts'
import { applyMomentScale } from '../../ogs/inference/plucker.ts'
import { HeadTeethEngine } from '../../ogs/inference/headTeethEngine.js'
import { resolveWorldRotation, worldRotationQuaternion } from '../../ogs/render/worldRotation.ts'
import { SplatMeshUpdater } from '../../ogs/render/SplatMeshUpdater.ts'
import { LocalFrameCamera } from '../../ogs/render/localFrameCamera.ts'
import { createCameraWatch } from '../../ogs/cameraWatch.ts'
import { useLightRelight } from './useLightRelight.js'
import { bootBranches, neutralPositions } from '../../ogs/pipeline/bootBranches.ts'
import { buildPartDrive } from '../../ogs/pipeline/partDriveBuilder.ts'
import { partDriveRegistry } from '../../ogs/preview/partDriveRegistry.js'
import { partRigUrl, PART_DRIVER_RIG } from '../../ogs/inference/partDriver.js'
import { neutralBounds } from '../../ogs/pipeline/framing.ts'
import { loadPoseTable } from '../../ogs/pipeline/poseTable.ts'
import { resolveRigConstraints } from '../../ogs/pipeline/rigConstraints.ts'
import { LiveRigPipeline } from '../../ogs/pipeline/LiveRigPipeline.ts'
import { resolveFaceDrivePlan, describeFaceDrivePlan } from '../../ogs/pipeline/faceDrivePlan.ts'
import { resolvePipelineKind } from '../../ogs/pipeline/pipelineKind.ts'
import { publishDebug, retractDebug } from '../../ogs/pipeline/ogsDebug.ts'
import { useAvatarRoot } from './AvatarRoot.jsx'
import { useRenderPlan } from './useRenderPlan.js'
import { useSplatFaceDrive } from './useSplatFaceDrive.js'
import BodyDrive, { SplatMovementTarget } from './BodyDrive.jsx'

const RENDERER_NODE_TYPE = 'plugin.ogs.renderer'

/** The terminal render node, from the plugin REGISTRY — core never imports plugins/. */
function createRendererNode(options) {
  const create = getPluginNode(RENDERER_NODE_TYPE)?.instance?.create
  if (typeof create !== 'function') {
    throw new Error(`the ${RENDERER_NODE_TYPE} plugin is not registered — nothing can present splats`)
  }
  return create(options)
}

export default function LiveRigAvatar({ boot, slug, log, animationBuffer, onReady, onError, onRenderBlocked }) {
  const gl = useThree((state) => state.gl)
  const scene = useThree((state) => state.scene)
  const camera = useThree((state) => state.camera)
  const root = useAvatarRoot()

  const localCam = useMemo(() => new LocalFrameCamera(), [])
  const watchCam = useMemo(() => new LocalFrameCamera(), [])
  const watch = useRef(null)
  if (!watch.current) watch.current = createCameraWatch()

  const pipeline = useRef(null)
  const started = useRef(false)
  const planRef = useRef(null)
  // The face path: all set asynchronously, so the frame hook reads them through getters.
  const rigDrive = useRef(null)
  const regions = useRef(null)
  const live = useRef({ cancelled: false, branches: null, updater: null, framer: null, avatarWasVisible: null })

  /** The bundle's rig.glb, registered LAZILY: whoever poses the body pays for the load. */
  const registerPartDrive = useCallback(({ engine, packs, scn, branches, worldRotation, worldScale, state, device }) => {
    const rigUrl = partRigUrl((name) => !!packs.getBytes(name), packs.texBase)
    if (!rigUrl) { log.info(`no ${PART_DRIVER_RIG} in this bundle — no body rig to pose`); return }

    const build = async () => {
      const poser = await buildPartDrive({
        rigUrl,
        branchMeshes: Object.fromEntries(scn.branches.map((branch) => [branch.name, branch.mesh])),
        branches,
        worldRotation,
        worldScale,
        // A live branch keeps its own deform's verts; the body reaches it as a rigid
        // delta on its ANCHOR, which the engine then re-poses.
        isLive: (branch) => branch.rigDim === engine.debug().rigDim,
        isCancelled: () => state.cancelled,
        // The LIFT device, so the body skinner's output lands in the buffer the
        // jacobians read. Without it every live branch keeps the rigid anchor —
        // the head lifting off the collar on a walk — which `renderContract.js`
        // now reports rather than leaving to be noticed on screen.
        device,
        log,
      })
      if (!poser || state.cancelled) return null
      engine.setPoser(poser)
      return {
        driveAt: (t) => engine.driveAt(t),
        duration: poser.duration,
        rig: poser.rig,
        rigToWorld: poser.rigToWorld,
        rigToWorldScale: poser.rigToWorldScale,
        posedTime: () => engine.landedTime,
      }
    }
    if (state.cancelled) return
    partDriveRegistry.register({ build })
  }, [log])

  const startPipeline = useCallback(async (plan) => {
    const state = live.current
    const { packs, scene: scn } = boot
    const flags = ogsGpuFlags()
    const manifest = scn.manifest
    const worldScale = manifest.worldScale
    const worldOffset = manifest.worldOffset
    const rotationDeg = resolveWorldRotation(manifest.worldRotation)
    const worldRotation = worldRotationQuaternion(rotationDeg)
    const momentScale = applyMomentScale(manifest.momentScale)
    log.info(`momentScale=${momentScale} worldScale=${worldScale} `
      + `worldOffset=[${worldOffset.join(', ')}] worldRotation=[${rotationDeg.join(', ')}]`)

    const providers = await resolveProviders(flags.ep, { preferWebgpu: true })
    if (state.cancelled) return
    const liftChoice = chooseLift({ subject: manifest.subject, character: slug }, flags.myraLift)
    log.info(`${liftChoice.impl} lift (${liftChoice.reason})`)

    const booted = await bootBranches({
      ort: ogsOrt,
      providers,
      flags,
      scene: scn,
      packs,
      precision: plan.precision,
      lift: liftChoice.impl,
      // Packed words: one present per rig update, and this pipeline has no SoA cull.
      packed: true,
      worldScale,
      worldOffset,
      worldRotation,
      onDowngrade: reportDowngrade,
      onBranches: (branches) => { state.branches = branches },
      isCancelled: () => state.cancelled,
      log,
    })
    if (!booted || state.cancelled) return

    const head = booted.branches.find((branch) => branch.name === 'head') ?? booted.branches[0]
    // The DNA's RAW control layer for the Rig Preview readout — only the head branch
    // carries a RigLogic instance, and it does not exist until the branches are built.
    setRigBoardRawLayer(head?.rawLayer ?? null)
    reportPrecision({
      requested: plan.precision,
      geom: head?.geomUsedFp16 ? 'fp16' : 'fp32',
      appr: head?.apprUsedFp16 ? 'fp16' : 'fp32',
    })

    // The bundle's own trained poses, then everything measured from them.
    const poseTable = await loadPoseTable({
      scene: packs.scene, fetchBytes: packs.fetchBytes, width: packs.rigNames?.length ?? 0, log,
    })
    if (state.cancelled) return
    const gather = packs.rigNames ? gatherFromRigNames(packs.rigNames) : null
    const constraints = resolveRigConstraints({
      poseTable, rigNames: packs.rigNames, scene: packs.scene, flags, gather,
    })
    for (const line of constraints.report.lines) log[line.level](line.text)
    publishDebug('__OGS_RIG_CONSTRAINTS__', constraints.report)

    const { liveBranches } = resolvePipelineKind({ manifest, rig: packs.rigPlan?.rig ?? 'none' })
    const drive = resolveFaceDrivePlan({
      manifest, rig: packs.rigPlan?.rig ?? 'none', liveBranches, graph: plan,
    })
    publishDebug('__OGS_FACE_DRIVE__', drive)
    for (const line of describeFaceDrivePlan(drive)) log.info(line)
    const declared = manifest.faceDrives ?? {}
    const asked = plan.faceDrive.liveMask
    regions.current = {
      brow: (declared.brow ?? true) && asked.brow,
      eye: (declared.eye ?? true) && asked.eye,
      cheek: (declared.cheek ?? true) && asked.cheek,
      nose: (declared.nose ?? true) && asked.nose,
    }

    const updater = new SplatMeshUpdater(root)
    updater.meshScale = worldScale
    updater.meshOffset = worldOffset
    updater.meshRotation = worldRotation
    state.updater = updater

    const frame = (preserveDirection) => {
      const bounds = neutralBounds(neutralPositions(booted.branches), { worldScale, worldOffset, worldRotation })
      if (!bounds) return false
      return fitCameraToSphere(camera, window.__ORBIT_CONTROLS__, bounds.centre, bounds.radius, { preserveDirection })
    }
    frame(false)
    state.framer = setContentFramer(() => frame(true))

    const glbAvatar = window.__AVATAR_SCENE__
    if (glbAvatar) { state.avatarWasVisible = glbAvatar.visible; glbAvatar.visible = false }

    const renderer = createRendererNode({
      id: 'renderer',
      updater,
      sources: booted.branches.map((branch) => ({
        name: branch.name,
        ports: branch.outputs.map((port) => port.name),
        lifter: branch.lifter,
        getVerts: () => branch.verts,
        capacity: branch.uvRes * branch.uvRes,
      })),
    })
    await renderer.init(booted.rendererCtx)
    if (state.cancelled) { updater.dispose(); return }

    const engine = new HeadTeethEngine({
      branches: booted.branches,
      camera: () => localCam.update(camera, root),
      renderer,
      onPresented: () => {
        // A pose is now ON SCREEN: the root is placed at this, never at the caller's clock.
        pipeline.current?.notePresented()
        if (pipeline.current) publishDebug('__OGS_PIPELINE__', pipeline.current.debug())
      },
      isActive: () => !state.cancelled,
    })

    const engineDrive = new LiveRigPipeline({
      engine,
      branches: booted.branches,
      constraints,
      precision: plan.precision,
      setVisible: (visible) => updater.setVisible(visible),
      onRefusedRigs: (refused) => reportRigProblem(slug, {
        cause: 'mismatch', detail: refused.map((r) => `${r.name}: ${r.error}`).join('; '),
      }),
      log,
      worldScale,
    })
    pipeline.current = engineDrive
    engineDrive.applyPlan(plan, null)
    const ready = await engineDrive.init()
    if (state.cancelled) return
    engineDrive.setVisible(true)
    for (const line of ready.lines) log.info(line)

    // The rig board addresses this control space directly — every control the decoders
    // know, not just the subset ARKit-52 can reach.
    registerRigBoard({
      names: packs.rigNames,
      scene: packs.scene,
      fetchBytes: packs.fetchBytes,
      setRig: (rig) => engineDrive.setRig(rig),
    })
    // The face path: ARKit -> this control space, composed through the constraint stages.
    rigDrive.current = gather
      ? { gather, constraints, push: (rig) => engineDrive.setRig(rig) }
      : null
    if (!gather) log.error('no rig_names.json — the face graph runs but no rig is pushed')
    setSplatEyeFollowCapability(constraints.eyeFollow
      ? { route: 'rig' }
      : {
        route: 'none',
        reason: "Not supported on this bundle — its control space (rig_names.json) has no "
          + 'eye-aim controls for the aim to drive.',
      })
    publishDebug('__OGS_PIPELINE__', engineDrive.debug())
    onReady?.()

    // The BODY rig, on this pipeline too. A MIXED bundle (a live head over a part-skinned
    // body) needs it, and without it such a character has a talking face and no way to
    // play a clip, a gesture or a generated walk at all.
    registerPartDrive({ engine: engineDrive, packs, scn, branches: booted.branches, worldRotation, worldScale, state, device: booted.device })
  }, [boot, camera, gl, localCam, log, onReady, registerPartDrive, root, scene, slug])

  const onPlan = useCallback((next, prev) => {
    planRef.current = next
    if (!started.current) {
      if (next.phase !== 'rendering') return
      started.current = true
      log.info('OGS Avatar wired — starting the render')
      startPipeline(next).catch((error) => {
        log.error('init failed', error)
        if (!live.current.cancelled) onError?.(error)
      })
      return
    }
    pipeline.current?.applyPlan(next, prev)
    pipeline.current?.setVisible(next.phase === 'rendering')
    if (pipeline.current) publishDebug('__OGS_PIPELINE__', pipeline.current.debug())
  }, [log, onError, startPipeline])

  useRenderPlan({ onPlan, onBlocked: onRenderBlocked, started: () => started.current })

  useSplatFaceDrive({
    animationBuffer,
    getRigDrive: () => rigDrive.current,
    getRegions: () => regions.current,
    active: () => !!pipeline.current && !live.current.cancelled,
  })

  useFrame(() => {
    const engine = pipeline.current
    if (!engine) return
    if (watch.current.movedLocal(watchCam.update(camera, root))) engine.runAppearance()
  })

  // The light is its own trigger; no cheap re-present here, so it pays an appr pass.
  useLightRelight({
    active: () => !!pipeline.current?.followsLight,
    avatar: () => root,
    onChanged: () => pipeline.current?.runAppearance(),
  })

  useEffect(() => {
    const state = live.current
    state.cancelled = false
    return () => {
      state.cancelled = true
      onRenderBlocked?.(null)
      state.framer?.()
      state.framer = null
      // A registration left standing hands the next character a builder over this
      // disposed scene.
      partDriveRegistry.reset()
      pipeline.current?.dispose()
      pipeline.current = null
      rigDrive.current = null
      regions.current = null
      watch.current?.reset()
      clearRigBoard()
      setSplatEyeFollowCapability(null)
      for (const branch of state.branches ?? []) {
        try { branch.dispose() } catch { /* best-effort: a leaked session beats a thrown teardown */ }
      }
      state.branches = null
      state.updater?.dispose()
      state.updater = null
      const glbAvatar = window.__AVATAR_SCENE__
      if (glbAvatar && state.avatarWasVisible !== null) glbAvatar.visible = state.avatarWasVisible
      state.avatarWasVisible = null
      retractDebug('__OGS_PIPELINE__')
      retractDebug('__OGS_FACE_DRIVE__')
      retractDebug('__OGS_RIG_CONSTRAINTS__')
    }
  }, [onRenderBlocked, scene])

  return (
    <>
      <BodyDrive slug={slug} getPlan={() => planRef.current} log={log} />
      <SplatMovementTarget />
    </>
  )
}
